import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { zodResolver } from "@hookform/resolvers/zod";
import { Accordion } from "@radix-ui/react-accordion";
import { FoldVertical, RefreshCcw, UnfoldVertical } from "lucide-react";
import { observer } from "mobx-react-lite";
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormProvider, useForm } from "react-hook-form";

import { OptionType, useGetOptionsQuery } from "@/api/admin";
import { invalidateEntityHistory } from "@/api/admin/auditLog";
import type { BranchesInBrand } from "@/api/admin/brands";
import {
  type BrandMenuGet,
  useDeleteMenuMutation,
  useGetBrandByIdQuery,
  useGetMenuQuery,
  useUpdateBrandMutation,
} from "@/api/admin/brands";
import {
  getCuisineIdsFromBrandProperties,
  mergeBrandPropertiesWithCuisine,
} from "@/api/admin/brands/brandPropertyUtils";
import { AdminSections } from "@/api/admin/teamRoles";
import { QUERY_KEYS } from "@/api/constants";
import { queryClient } from "@/api/queryClient";
import {
  CustomButton,
  CustomCardBody,
  CustomLabel,
  Loader,
} from "@/components/atoms";
import {
  AddDish,
  DishesBlock,
  MultiSelectWithTags,
  toastError,
  toastSuccess,
} from "@/components/molecules";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { MENU_HISTORY_FIELDS_MAPPING } from "@/modules/AdminAuditLog/utils/getEntityFieldsMapping";
import { useWizardStepDraft } from "@/modules/wizardDraft";
import { usePermission } from "@/services/casl/usePermission";
import { rootStore } from "@/store/RootStore";
import type { FormProps, FormRefType } from "@/types/common";
import { commonIconProps12 } from "@/utils/constants";
import { DEFAULT_NAME } from "@/utils/constants/defaultNames";
import { useExposeFormValues } from "@/utils/hooks/useExposeFormValues";

import { DishDrawer } from "../DishDrawer";
import { EntityHistory } from "../EntityHistory";
import { MENU_HISTORY_FIELD_RESOLVERS } from "../EntityHistory/utils/fieldResolvers";
import DialogCategory from "./components/DialogCategory";
import SortableCategoryBlock from "./components/SortableCategoryBlock";
import {
  MenuActionsContext,
  useMenuActions,
} from "./menuActions/MenuActionsContext";
import { useMenuActionsProvider } from "./menuActions/useMenuActionsProvider";
import { isMenuReadyToSave } from "./menuMappers";
import TabsMenuConnector from "./MenuUploader/TabsMenuConnector";
import {
  type BrandFormMenuType,
  makeFormSchema,
  WizardMenuFormSchema,
} from "./types";

interface BrandFormMenuExtraProps {
  branches?: BranchesInBrand[];
  brandId?: string;
  wizardMode?: boolean;
  onMenuReset?: () => void | Promise<void>;
  onMenuUpdated?: (menu: BrandMenuGet) => void;
  draftEntityId?: string;
}

const EMPTY_MENU: BrandFormMenuType = {
  cuisine: [],
  categories: [],
  file: undefined,
  url: "",
};

const removeIgnoredValuesFromDraft = (
  values: BrandFormMenuType
): BrandFormMenuType => {
  const draftValues: Partial<BrandFormMenuType> = { ...values };

  delete draftValues.cuisine;
  delete draftValues.categories;
  delete draftValues.file;

  return draftValues as BrandFormMenuType;
};

type BrandFormMenuInnerType = Omit<FormProps<BrandFormMenuType>, "onSubmit"> &
  BrandFormMenuExtraProps & {
    onSubmit?: (data: BrandFormMenuType) => void;
    id?: string;
  };

const BrandFormMenuInner = forwardRef<
  FormRefType<BrandFormMenuType>,
  BrandFormMenuInnerType
>(
  (
    {
      onSubmit,
      onValidityChange,
      id,
      classes,
      defaultValues,
      branches,
      brandId,
      wizardMode = false,
      onMenuReset,
      onMenuUpdated,
      draftEntityId,
    },
    ref
  ) => {
    const [isScratch, setIsScratch] = useState(false);
    const [isExtracted, setIsExtracted] = useState(false);
    const [isResetScratchOpen, setIsResetScratchOpen] = useState(false);
    const [expandAll, setExpandAll] = useState(false);
    const [openCategoryIds, setOpenCategoryIds] = useState<string[]>([]);

    const prevCuisineRef = useRef<string[] | undefined>(undefined);
    const skipCuisinePatchRef = useRef(true);

    const { mutateAsync: deleteMenu, isPending: isDeletingMenu } =
      useDeleteMenuMutation();

    const { data: brandData } = useGetBrandByIdQuery(brandId ?? "");
    const { mutateAsync: updateBrand } = useUpdateBrandMutation();

    const parentMenuActions = useMenuActions();
    const ownedMenuActions = useMenuActionsProvider(brandId, { onMenuUpdated });
    const menuActions = parentMenuActions.isEnabled
      ? parentMenuActions
      : ownedMenuActions;

    const { data: menuData } = useGetMenuQuery(brandId ?? "");

    const hasUpdatePermission = usePermission(AdminSections.BRAND, {
      isUpdate: true,
    });

    const { isBranchAccess } = rootStore.authStore;
    const menuStore = rootStore.menuStore;

    // Prices on this menu are written in the brand's currency, which decides the
    // symbol shown beside a price and how many decimals the field accepts. Set
    // here rather than in a parent because every screen that shows a menu
    // renders this component, and each parent would otherwise have to remember.
    useEffect(() => {
      menuStore.setCurrency(menuData?.currency ?? null);
    }, [menuData?.currency, menuStore]);

    const priceDecimals = menuStore.priceDecimals;

    // Sync API branches into the store whenever they change
    const prevBranchesRef = useRef<BranchesInBrand[] | undefined>(undefined);
    useEffect(() => {
      if (!branches) return;
      if (prevBranchesRef.current === branches) return;
      prevBranchesRef.current = branches;
      menuStore.setBranches(
        branches.map(b => ({
          branchId: b.branchId,
          branchName: b.branchName,
          city: b.cityName,
        }))
      );
    }, [branches, menuStore]);

    const {
      data: optionsData,
      isPending: isOptionPending,
      isSuccess,
    } = useGetOptionsQuery([OptionType.Cuisines]);

    // Prices are checked against the brand's currency, so a Jordanian menu of
    // 2.750 dishes saves instead of failing on every price. The currency
    // arrives with the menu, so this is rebuilt when it lands - react-hook-form
    // reads `_options` on each render, so the new resolver takes effect.
    const menuFormSchema = useMemo(
      () => (wizardMode ? WizardMenuFormSchema : makeFormSchema(priceDecimals)),
      [wizardMode, priceDecimals]
    );

    const form = useForm<BrandFormMenuType>({
      resolver: zodResolver(menuFormSchema),
      defaultValues,
      mode: "onChange",
      reValidateMode: "onChange",
    });

    useWizardStepDraft({
      form,
      wizard: "brand",
      entityId: draftEntityId,
      step: "menu",
      transformValues: removeIgnoredValuesFromDraft,
    });

    useExposeFormValues(ref, form);

    const {
      watch,
      formState: { isValid },
      handleSubmit,
      reset,
    } = form;

    const brandPropertiesForInitialization = draftEntityId
      ? undefined
      : brandData?.properties;

    useEffect(() => {
      if (!defaultValues) return;

      const cuisine = brandPropertiesForInitialization
        ? getCuisineIdsFromBrandProperties(brandPropertiesForInitialization)
        : (defaultValues.cuisine ?? []);

      const nextValues = { ...defaultValues, cuisine };
      menuStore.initMenu(nextValues);
      reset(nextValues);
      skipCuisinePatchRef.current = true;
      prevCuisineRef.current = cuisine;
    }, [brandPropertiesForInitialization, defaultValues, menuStore, reset]);

    const didInitScratchFromBackendRef = useRef(false);

    useEffect(() => {
      if (
        !defaultValues ||
        didInitScratchFromBackendRef.current ||
        isExtracted
      ) {
        return;
      }

      const hasBackendMenu = (defaultValues.categories?.length ?? 0) > 0;

      if (hasBackendMenu) {
        setIsScratch(true);
        didInitScratchFromBackendRef.current = true;
      }
    }, [defaultValues, isExtracted]);

    const categories = watch("categories") ?? [];
    const hasMenuData = categories.length > 0;

    useEffect(() => {
      const subscription = form.watch(values => {
        menuStore.setMenu(values as Partial<BrandFormMenuType>);
      });
      return () => subscription.unsubscribe();
    }, [form, menuStore]);

    const hasDuplicateCategories = (menuStore.menu.categories ?? []).some(
      c => c.isDuplicate === true
    );

    const menuReady = isMenuReadyToSave(menuStore.menu);

    const menuStepValid =
      menuReady && !hasDuplicateCategories && (wizardMode || isValid);

    const handleSubmitData = () => {
      if (hasDuplicateCategories || !menuReady) return;
      if (!wizardMode && !isValid) return;
      onSubmit?.(menuStore.menu);
    };

    useEffect(() => {
      onValidityChange?.(menuStepValid);
    }, [menuStepValid, onValidityChange]);

    const [sortedCategoryIds, setSortedCategoryIds] = useState<string[]>(() =>
      categories.map(c => c.id).filter((id): id is string => !!id)
    );

    useEffect(() => {
      const storeIds = (menuStore.menu.categories ?? [])
        .map(c => c.id)
        .filter((id): id is string => !!id);
      const current = sortedCategoryIds.join(",");
      const incoming = storeIds.join(",");
      if (current !== incoming) {
        setSortedCategoryIds(storeIds);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [menuStore.menu.categories]);

    useEffect(() => {
      if (sortedCategoryIds.length === 0) {
        setOpenCategoryIds([]);
        setExpandAll(false);
        return;
      }

      setOpenCategoryIds(prev => {
        if (expandAll) {
          return sortedCategoryIds;
        }

        return prev.filter(id => sortedCategoryIds.includes(id));
      });
    }, [expandAll, sortedCategoryIds]);

    const handleToggleExpandAll = () => {
      if (expandAll) {
        setOpenCategoryIds([]);
        setExpandAll(false);
        return;
      }

      setOpenCategoryIds(sortedCategoryIds);
      setExpandAll(true);
    };

    const handleAccordionChange = (value: string[]) => {
      setOpenCategoryIds(value);
      setExpandAll(
        sortedCategoryIds.length > 0 &&
          value.length === sortedCategoryIds.length
      );
    };

    const sensors = useSensors(useSensor(PointerSensor));

    const cuisine = watch("cuisine");
    const canShowResetMenu = (menuStore.menu.categories ?? []).length > 0;
    const showResetMenuButton =
      canShowResetMenu && (isScratch || (hasMenuData && !isExtracted));

    useEffect(() => {
      if (skipCuisinePatchRef.current) {
        skipCuisinePatchRef.current = false;
        return;
      }

      if (!brandId || !cuisine) return;
      if (
        JSON.stringify(prevCuisineRef.current ?? []) === JSON.stringify(cuisine)
      ) {
        return;
      }

      const previousCuisine = prevCuisineRef.current ?? [];
      const previousSet = new Set(previousCuisine);
      const nextSet = new Set(cuisine);
      const addedCount = cuisine.filter(id => !previousSet.has(id)).length;
      const removedCount = previousCuisine.filter(
        id => !nextSet.has(id)
      ).length;

      prevCuisineRef.current = cuisine;

      updateBrand({
        id: brandId,
        data: {
          properties: mergeBrandPropertiesWithCuisine(
            brandData?.properties,
            cuisine
          ),
        },
      })
        .then(() => {
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.brands, brandId],
          });
          invalidateEntityHistory({ scope: brandId });
          if (addedCount > 0 && removedCount > 0) {
            toastSuccess("Cuisines updated successfully");
          } else if (addedCount > 0) {
            toastSuccess("Cuisine added successfully");
          } else if (removedCount > 0) {
            toastSuccess("Cuisine removed successfully");
          }
        })
        .catch(error => {
          toastError(
            error instanceof Error
              ? error.message
              : "Failed to update cuisines",
            () => {},
            "Error"
          );
        });
    }, [brandData?.properties, brandId, cuisine, updateBrand]);

    const handleCategoryDragEnd = async (event: DragEndEvent) => {
      const { active, over } = event;
      if (active.id !== over?.id) {
        const oldIndex = sortedCategoryIds.indexOf(active.id as string);
        const newIndex = sortedCategoryIds.indexOf(over?.id as string);
        const newSorted = arrayMove(sortedCategoryIds, oldIndex, newIndex);
        setSortedCategoryIds(newSorted);

        try {
          await menuActions.reorderCategories(newSorted);
          await menuActions.invalidateMenuQuery();
          form.reset(menuStore.menu);
        } catch (error) {
          toastError(
            error instanceof Error
              ? error.message
              : "Failed to reorder categories",
            () => {},
            "Error"
          );
        }
      }
    };

    const handleDrawerSave = useCallback(
      async (
        dish: Parameters<typeof menuStore.onDrawerSave>[0],
        branchAvailability: Parameters<typeof menuStore.onDrawerSave>[1]
      ) => {
        const categoryId =
          menuStore.dishDrawer.categoryKey ?? menuStore.currentCategoryId;
        if (!categoryId) return;

        try {
          await menuActions.saveDish(
            categoryId,
            dish,
            branchAvailability,
            menuStore.dishDrawer.mode
          );
          await menuActions.invalidateMenuQuery();
          form.reset(menuStore.menu);
        } catch (error) {
          toastError(
            error instanceof Error ? error.message : "Failed to save dish",
            () => {},
            "Error"
          );
          throw error;
        }
      },
      [form, menuActions, menuStore]
    );

    const handleDrawerDelete = useCallback(async () => {
      const dishId = menuStore.dishDrawer.dish.id;
      const categoryId =
        menuStore.dishDrawer.categoryKey ?? menuStore.currentCategoryId;
      if (!dishId || !categoryId) return;

      try {
        await menuActions.deleteDish(categoryId, dishId);
        await menuActions.invalidateMenuQuery();
        form.reset(menuStore.menu);
      } catch (error) {
        toastError(
          error instanceof Error ? error.message : "Failed to delete dish",
          () => {},
          "Error"
        );
        throw error;
      }
    }, [form, menuActions, menuStore]);

    const handleEditDish = useCallback(
      async (
        categoryId: string,
        dish: Parameters<typeof menuStore.openEditDish>[1]
      ) => {
        if (menuActions.isEnabled && dish.id) {
          try {
            const freshDish = await menuActions.fetchDish(dish.id);
            if (freshDish) {
              const branchAvailability = menuStore.branches.map(b => ({
                ...b,
                available: !(freshDish.unavailableBranchIds ?? []).includes(
                  b.branchId
                ),
              }));
              menuStore.addOrUpdateDishToCurrentCategory(
                freshDish,
                branchAvailability,
                categoryId
              );
              menuStore.openEditDish(categoryId, freshDish);
              return;
            }
          } catch (error) {
            toastError(
              error instanceof Error
                ? error.message
                : "Failed to load dish details.",
              () => {},
              "Error"
            );
          }
        }
        menuStore.openEditDish(categoryId, dish);
      },
      [menuActions, menuStore]
    );

    const resetLocalMenu = () => {
      rootStore.menuStore.clearMenu();
      reset(EMPTY_MENU);
      setSortedCategoryIds([]);
      setOpenCategoryIds([]);
      setExpandAll(false);
      setIsExtracted(false);
      setIsResetScratchOpen(false);
      setIsScratch(false);
      didInitScratchFromBackendRef.current = false;
    };

    const handleResetMenu = async () => {
      try {
        if (brandId) {
          await deleteMenu(brandId);
        }

        resetLocalMenu();
        await onMenuReset?.();
      } catch (error) {
        toastError(
          error instanceof Error ? error.message : "Failed to reset menu",
          () => {
            handleResetMenu();
          },
          "Error"
        );
        throw error;
      }
    };

    if (isOptionPending || !optionsData || !isSuccess) {
      return <Loader fullscreen />;
    }

    const orderedCategories = sortedCategoryIds
      .map(id => (menuStore.menu.categories ?? []).find(c => c.id === id))
      .filter((c): c is NonNullable<typeof c> => !!c);

    const formContent = (
      <FormProvider {...form}>
        <Form {...form}>
          <form
            onSubmit={handleSubmit(handleSubmitData)}
            id={id}
            data-testid="brand-form-menu"
          >
            <CustomCardBody
              classes={cn("relative flex flex-col gap-6", classes)}
            >
              <EntityHistory
                entityId={brandId}
                entityName={menuData?.name ?? DEFAULT_NAME}
                blockName="Menu"
                wrapperClass="absolute right-6 -top-4"
                mapKeyToName={MENU_HISTORY_FIELDS_MAPPING}
                fieldResolvers={MENU_HISTORY_FIELD_RESOLVERS}
                includeEntityNameInTitle
              />
              {!isBranchAccess && (
                <div className="flex justify-between items-end gap-4">
                  <div className="w-full sm:w-[51%]">
                    <CustomLabel htmlFor="cuisine" label="Cuisine" />
                    <MultiSelectWithTags
                      form={form}
                      nameSelect="cuisine"
                      tagsList={optionsData?.cuisines || []}
                      disabled={!hasUpdatePermission}
                    />
                  </div>
                  {showResetMenuButton && (
                    <CustomButton
                      variant="light"
                      onClick={e => {
                        e.preventDefault();
                        setIsResetScratchOpen(true);
                      }}
                      disabled={!hasUpdatePermission}
                    >
                      <RefreshCcw className="w-4 h-4" />
                      Reset menu
                    </CustomButton>
                  )}
                </div>
              )}

              {!isScratch && (
                <TabsMenuConnector
                  brandId={brandId}
                  setIsScratch={setIsScratch}
                  isExtracted={isExtracted}
                  setIsExtracted={setIsExtracted}
                  onMenuReset={handleResetMenu}
                  isResettingMenu={isDeletingMenu}
                />
              )}

              {(isScratch || isExtracted || hasMenuData) && (
                <>
                  <div className="flex flex-col gap-4">
                    {hasUpdatePermission && (
                      <DialogCategory
                        form={form}
                        nameSelect="categories"
                        disabled={
                          (watch("cuisine") ?? []).length === 0 &&
                          !isBranchAccess
                        }
                      />
                    )}
                    <div className="flex items-center justify-between gap-4">
                      <p className="text-sm text-gray-500">
                        We will only display the categories in which you have
                        added dishes on your restaurant profile.
                      </p>
                    </div>
                    {orderedCategories.length > 0 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleToggleExpandAll}
                        className="flex w-fit ml-auto items-center text-xs hover:bg-transparent cursor-pointer"
                        data-testid="brand-form-menu-expand-all"
                      >
                        {expandAll ? (
                          <FoldVertical {...commonIconProps12} />
                        ) : (
                          <UnfoldVertical {...commonIconProps12} />
                        )}
                        {expandAll ? "Collapse all" : "Expand all"}
                      </Button>
                    )}
                  </div>

                  <>
                    {!hasMenuData && (
                      <div
                        className="flex items-center justify-center rounded-md min-h-32 bg-gray-50 text-sm text-gray-500 border border-gray-200"
                        data-testid="brand-form-menu-empty-category"
                      >
                        <span>Add categories to add dishes to the menu</span>
                      </div>
                    )}

                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleCategoryDragEnd}
                    >
                      <SortableContext
                        items={sortedCategoryIds}
                        strategy={verticalListSortingStrategy}
                      >
                        <Accordion
                          type="multiple"
                          value={openCategoryIds}
                          onValueChange={handleAccordionChange}
                          className="flex flex-col gap-4 pt-2"
                        >
                          {orderedCategories?.map(category => (
                            <SortableCategoryBlock
                              key={category.id}
                              id={category.id || ""}
                              categoryNameEn={category.categoryNameEn}
                              categoryNameAr={category.categoryNameAr}
                              form={form}
                              hasUpdatePermission={hasUpdatePermission}
                            >
                              {category.dishes && category.dishes.length > 0 ? (
                                <DishesBlock
                                  category={category.id || ""}
                                  positions={category.dishes}
                                  onEditClick={dish => {
                                    handleEditDish(category.id || "", dish);
                                  }}
                                  onCreateClick={() => {
                                    menuStore.openCreateDish(category.id || "");
                                  }}
                                  hasUpdatePermission={!hasUpdatePermission}
                                />
                              ) : (
                                <AddDish
                                  onClick={() => {
                                    menuStore.openCreateDish(category.id || "");
                                  }}
                                  classes="ml-10 mb-3"
                                  hasUpdatePermission={!hasUpdatePermission}
                                />
                              )}
                            </SortableCategoryBlock>
                          ))}
                        </Accordion>
                      </SortableContext>
                    </DndContext>
                  </>
                </>
              )}
            </CustomCardBody>
          </form>
        </Form>

        <DishDrawer
          store={menuStore.dishDrawer}
          priceDecimals={menuStore.priceDecimals}
          currencySymbol={menuStore.currency?.symbol}
          onSave={handleDrawerSave}
          onDelete={handleDrawerDelete}
        />

        <Dialog open={isResetScratchOpen} onOpenChange={setIsResetScratchOpen}>
          <DialogContent className="sm:max-w-[420px]">
            <div className="flex flex-col items-center text-center">
              <div className="p-3 bg-red-50 rounded-full mb-4">
                <RefreshCcw className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-xl font-bold mb-2">Reset your menu?</h2>
              <div className="text-gray-500 mb-6 text-sm">
                <p>
                  Every category, dish, photo, and branch setting will be
                  removed. This action can&apos;t be undone.
                </p>
              </div>
              <div className="flex gap-3 w-full">
                <CustomButton
                  variant="light"
                  className="flex-1"
                  onClick={() => setIsResetScratchOpen(false)}
                >
                  Keep this menu
                </CustomButton>
                <CustomButton
                  variant="destructive"
                  className="flex-1 items-center"
                  onClick={() => {
                    handleResetMenu();
                  }}
                  disabled={isDeletingMenu}
                  isPending={isDeletingMenu}
                >
                  Reset
                </CustomButton>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </FormProvider>
    );

    if (parentMenuActions.isEnabled) {
      return formContent;
    }

    return (
      <MenuActionsContext.Provider value={ownedMenuActions}>
        {formContent}
      </MenuActionsContext.Provider>
    );
  }
);

BrandFormMenuInner.displayName = "BrandFormMenu";

const BrandFormMenu = observer(BrandFormMenuInner);
export default BrandFormMenu;
